import { Edit, Trash2, Mail } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
} from "@f2e/ui/components/base"

import { OAuthProviderLogo } from "@/components"
import { BrandOAuthProvider } from "@/types"

export interface OAuthProviderTableProps {
  providers: BrandOAuthProvider[]
  canEdit?: boolean
  onEdit?: (provider: BrandOAuthProvider) => void
  onDelete?: (provider: BrandOAuthProvider) => void
}

const OAuthProviderTable = ({
  providers,
  canEdit = false,
  onEdit,
  onDelete,
}: OAuthProviderTableProps) => {
  const { t } = useTranslation()

  if (providers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Mail className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">{t("brands.oauth.noProviders")}</p>
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/6">
              {t("brands.oauth.provider")}
            </TableHead>
            <TableHead className="w-1/4">{t("brands.oauth.name")}</TableHead>
            <TableHead className="w-1/4">
              {t("brands.oauth.clientId")}
            </TableHead>
            <TableHead className="w-1/4">{t("brands.oauth.scopes")}</TableHead>
            {canEdit && (
              <TableHead className="w-1/6 text-center">
                {t("brands.oauth.actions")}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {providers.map((provider) => (
            <TableRow key={provider.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <OAuthProviderLogo provider={provider.provider} />
                  <span className="font-medium capitalize">
                    {provider.provider}
                  </span>
                </div>
              </TableCell>
              <TableCell>{provider.name}</TableCell>
              <TableCell className="font-mono text-sm">
                {provider.clientId}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {provider.scopes.slice(0, 3).map((scope) => (
                    <Badge key={scope} variant="secondary" className="text-xs">
                      {scope}
                    </Badge>
                  ))}
                  {provider.scopes.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{provider.scopes.length - 3}
                    </Badge>
                  )}
                </div>
              </TableCell>
              {canEdit && (
                <TableCell className="text-center">
                  <div className="flex justify-center gap-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit?.(provider)}
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">{t("brands.oauth.edit")}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete?.(provider)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                      <span className="sr-only">
                        {t("brands.oauth.delete")}
                      </span>
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default OAuthProviderTable
